import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useQuery } from 'react-query';
import { useForm } from './useForm';
import _ from 'lodash';
import { useMessage } from '../context/useMessage';
import { SortElement } from '../shared/list';
import { converSortToStrings, convertStringsToSort } from '../utils/sort';

export interface SearchFnReturn<T> {
  list: T[];
  count: number;
}

export interface ApiOptions {
  cacheTime?: number;
  refetchInterval?: number;
}

export interface useBaseListProps<T, F> {
  searchFn: (
    page: number,
    pageSize: number,
    filters: F,
    sort?: string[],
    signal?: AbortSignal,
  ) => Promise<SearchFnReturn<T>>;
  overrideFilters?: F;
  defaultFilters?: F;
  filterStoreKey: string;
  initPageSize?: number;
  apiOptions?: ApiOptions;
}

export const useBaseList = <T, F>({
  searchFn,
  overrideFilters,
  defaultFilters,
  filterStoreKey,
  initPageSize = 15,
  apiOptions,
}: useBaseListProps<T, F>) => {
  //Paginazione
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initPageSize);

  //Filtri
  const [cookies, setCookie] = useCookies([`fltrs-${filterStoreKey}`]);

  const [filters, setFilters] = useState<F>(overrideFilters ?? cookies[`fltrs-${filterStoreKey}`] ?? defaultFilters);

  //Sort
  const [sort, setSort] = useState<string[]>([]);

  useEffect(() => {
    if (filters) {
      setCookie(`fltrs-${filterStoreKey}`, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const { error } = useMessage()

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPage(0);
    setPageSize(newPageSize);
  };

  const { data, isLoading, isFetching, refetch, isPreviousData } = useQuery(
    [filterStoreKey, page, pageSize, { filters, sort }],
    ({ signal }) => searchFn(page, pageSize, filters, sort, signal),
    {
      ...apiOptions,
      keepPreviousData: true,
      onError(e: Error) {
        error({ title: e.message });
      },
      staleTime: 600000,
    },
  );

  const submitFilters = useCallback(
    (fltrs: F) => {
      if (!_.isEqual(fltrs, filters)) {
        setFilters(fltrs);
        setPage(0);
      } else if (!_.isEqual(page, 0)) {
        setPage(0);
      } else {
        refetch();
      }
    },
    [filters, page, refetch],
  );

  const filtersForm = useForm<F>({
    defaultValues: filters,
    submitOnEnter: true,
    onSubmit: submitFilters,
  });

  const onSortClick = (clicked: SortElement<T>) => {
    let ret = [...sort];

    let temp = convertStringsToSort(ret);
    if (temp.some((s) => s.key === clicked.key)) {
      if (clicked.type === 'desc') {
        ret = ret.filter((s) => {
          const parts = s.split(',');
          return parts[0] !== clicked.key;
        });
      } else {
        ret = ret.map((s) => {
          const parts = s.split(',');
          if (parts[0] === clicked.key) {
            return [clicked.key, 'desc'].join(',');
          } else {
            return s;
          }
        });
      }
    } else {
      ret.push(...converSortToStrings([{ key: clicked.key, type: 'asc' }]));
    }
    setSort(ret);
  };

  const clearFilters = () => {
    setFilters(defaultFilters ?? ({} as F));
    setPage(0);
  };

  return {
    list: data?.list ?? [],
    page,
    pageSize,
    count: data?.count ?? 0,
    isLoading,
    isFetching,
    isPreviousData: isPreviousData || isFetching,
    filtersForm,
    filters,
    handlePageChange,
    handlePageSizeChange,
    search: refetch,
    setFilters,
    clearFilters,
    sort,
    onSortClick,
  };
};
